import React, { useEffect, useState, useMemo, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { printQrPoster } from "../lib/qrPoster";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Copy, MapPin, Users, AlertTriangle, Home, QrCode } from "lucide-react";
import { Link } from "react-router-dom";
import { CellCallReport } from "../components/CellCallReport";
import { GroupReplies } from "../components/GroupReplies";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function CellOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const qrRef = useRef(null);
  const formUrl = `${window.location.origin}/cell-response`;
  const printPoster = () => printQrPoster(qrRef.current?.querySelector("canvas"), "Join a Cell Group",
    "Tell us where you live and we'll connect you with a cell group near you.", formUrl,
    ["1. Scan the code", "2. Fill in your details", "3. We'll match you to a group"]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/cell/responses"); setRows(data.responses || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const areas = {};
    rows.forEach((r) => { if (r.outward) areas[r.outward] = (areas[r.outward] || 0) + 1; });
    const largest = Object.entries(areas).sort((a, b) => b[1] - a[1]);
    return {
      total: rows.length,
      hosts: rows.filter((r) => r.willing_to_host || r.host_status === "Confirmed").length,
      confirmedHosts: rows.filter((r) => r.host_status === "Confirmed").length,
      areas: Object.keys(areas).length,
      needsReview: rows.filter((r) => !r.excluded && (!r.postcode_valid || r.is_duplicate)).length,
      withAvailability: rows.filter((r) => r.availability).length,
      largest,
    };
  }, [rows]);

  const briefing = useMemo(() => {
    const top = stats.largest.slice(0, 3).map(([a, n]) => `${a} (${n})`).join(", ");
    return `Streams of Joy Erith London — Cell Group briefing.\n` +
      `${stats.total} responses received across ${stats.areas} postcode areas. ` +
      `${stats.hosts} people are potential hosts (${stats.confirmedHosts} confirmed). ` +
      `${stats.withAvailability} have recorded their availability. ` +
      `${stats.needsReview} record(s) need review before planning. ` +
      (top ? `Largest clusters: ${top}.` : "");
  }, [stats]);

  const copySummary = async () => {
    try { await navigator.clipboard.writeText(briefing); toast.success("Briefing copied"); }
    catch { toast.error("Could not copy"); }
  };

  const max = stats.largest.length ? stats.largest[0][1] : 1;
  const cards = [
    ["Responses", stats.total, "Signup records", Users],
    ["Potential hosts", stats.hosts, `${stats.confirmedHosts} confirmed`, Home],
    ["Postcode areas", stats.areas, "Valid outward codes", MapPin],
    ["Availability set", stats.withAvailability, `of ${stats.total} responses`, Users],
    ["Needs review", stats.needsReview, "Quality checks", AlertTriangle],
  ];

  return (
    <div className="space-y-6" data-testid="cell-overview-page">
      <div className="sj-hero p-7 sm:p-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="sj-eyebrow">Streams of Joy Erith London</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold mt-1 leading-tight">Cell Group Response Dashboard</h1>
          <p className="text-sm mt-2 text-white/85">Postcode intelligence, hosting interest and follow-up readiness.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="cell-overview-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-xl border border-white/30 px-4 py-2.5 text-sm font-semibold hover:bg-white/10"><RefreshCw className="h-4 w-4" /> Refresh data</button>
          <button data-testid="cell-overview-qr-poster" onClick={printPoster} className="inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold" style={{ color: "var(--sj-primary)" }}><QrCode className="h-4 w-4" /> Form QR poster</button>
          <div ref={qrRef} className="hidden"><QRCodeCanvas value={formUrl} size={600} level="M" fgColor="#063B30" /></div>
          <button data-testid="cell-overview-copy" onClick={copySummary} className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: "var(--sj-accent)", color: "#063B30" }}><Copy className="h-4 w-4" /> Copy summary</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map(([label, n, sub, Icon]) => (
          <div key={label} className={`${CARD} p-4`} data-testid={`cell-overview-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Icon className="h-5 w-5 mb-1.5" style={{ color: "var(--sj-primary)" }} />
            <div className="text-3xl font-extrabold font-head" style={{ color: "var(--sj-primary)" }}>{n}</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{label}</div>
            <div className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className={`${CARD} p-5`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head text-lg font-semibold">Largest postcode groups</h3>
            <Link to="/cell/postcode-groups" className="text-sm font-medium" style={{ color: "var(--sj-primary)" }}>View all</Link>
          </div>
          {loading ? <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Loading…</p> : stats.largest.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No responses yet.</p>
          ) : (
            <div className="space-y-2">
              {stats.largest.slice(0, 7).map(([area, n]) => (
                <Link key={area} to={`/cell/responses?area=${encodeURIComponent(area)}`} className="block" data-testid={`cell-overview-area-${area}`}>
                  <div className="flex items-center justify-between text-sm mb-0.5"><span className="font-medium">{area}</span><span style={{ color: "var(--sj-text-3)" }}>{n}</span></div>
                  <div className="h-2 rounded-full" style={{ background: "#EDF2F4" }}><div className="h-2 rounded-full" style={{ width: `${(n / max) * 100}%`, background: "var(--sj-primary)" }} /></div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className={`${CARD} p-5`} data-testid="cell-overview-briefing">
          <h3 className="font-head text-lg font-semibold mb-3">Pastor briefing</h3>
          <p className="text-sm whitespace-pre-line leading-relaxed" style={{ color: "var(--sj-text-2)" }}>{briefing}</p>
        </div>
      </div>
      <CellCallReport />
      <div className={`${CARD} p-6`} data-testid="cell-overview-replies"><GroupReplies endpoint="/cell/group-replies" showGroup testid="cell-admin-replies" className="" title="Members' replies to host group texts" /></div>
    </div>
  );
}
